import type { TileSet } from './tiles';
import { pairs, type WorldMapSpec } from './worldSpec';

/** Sunscar Sands' painting (approved 29 October), tiled as public/world/egypt/v1
 *  at WebP Q74 (the highest at which the opening view stays inside its 1.5 MB
 *  budget measured both ways: levels 0 to 2 in full plus the level-3 columns of the
 *  view, 1,309 KB at 390x844 and 1,531 KB at 375x667; and tile-map.sh's own worst
 *  case, 1,189 KB. Q75 is 1,324, 1,548 and 1,203 KB). The world's key
 *  in code is `egypt` (the biome id); its approved art folder is `sands`. A
 *  changed painting goes in a new version folder, with its spots measured again. */
export const EGYPT_TILES: TileSet = {
  base: '/world/egypt/v1/map_files',
  width: 3072,
  height: 2048,
  tileSize: 512,
  top: 3,
};

/**
 * Sunscar Sands, from the painting's spots.json: pale gold dunes, palms and a
 * river, its seven buildings along a stone-flagged road (node ids 36 to 42, the
 * world's slots in order). The pyramid's, the palace's, the Anubis gate's and the
 * sun temple's outlines hug thin structures (the apex, the palms and columns
 * round the lagoon, the jackal's ears and the obelisks), so those four carry a
 * padded `tap` shape a finger can hit; the others are tapped by their outlines.
 * The palace's landing stage and its posts are a still area (the river's ripple
 * and the pieces keep off it).
 *
 * Every plate hangs where the painting leaves room beside or above its
 * building (Jon, 26 October), clear of every outline, the pieces' places and
 * the road, and where a phone's view reaches it with the building in the middle
 * of the screen (the top bar and the button columns hide a plate they cover):
 * above the Desert Caravan, Pharaoh's Riches, the Book of the Dead and the Eye of
 * Ra (which stand close to the road), and below the pyramid, the palace (over the
 * lagoon, past the landing stage) and the Anubis gate. Pharaoh's Riches and the
 * Eye of Ra carry a narrow two-line plate on the smallest phone, where a wide one
 * cannot clear the roof and the buttons. The Anubis gate's two horses moved to
 * make room for its plate (life/egypt.py). There is no painted lamp along the road
 * (the six braziers at the doors are lit in code, tied to their slots), so the
 * posts the code draws stand where each was proved on open sand beside the
 * road (a 2x crop at every foot, 30 October): off the flagstones and their edge,
 * the stairs and pedestals, plants, rocks, the buildings, the plates and the
 * pieces' places.
 */
export const EGYPT: WorldMapSpec = {
  biome: 'egypt',
  tiles: EGYPT_TILES,
  buildings: [
    {
      node: 36,
      name: 'Desert Caravan',
      centre: [300, 720],
      plate: [376, 380],
      plateTight: [480, 346],
      outline: pairs([
        72, 784, 80, 710, 124, 656, 196, 596, 216, 536, 250, 524, 292, 492, 320, 530, 370, 570, 430,
        570, 490, 600, 528, 660, 536, 704, 524, 770, 480, 836, 430, 856, 360, 876, 280, 892, 200,
        888, 130, 864, 90, 828,
      ]),
    },
    {
      node: 37,
      name: "Pharaoh's Tomb",
      centre: [860, 960],
      plate: [880, 1172],
      plateTight: [824, 1172],
      outline: pairs([
        820, 664, 880, 744, 960, 850, 1040, 960, 1100, 1030, 1104, 1080, 1040, 1140, 980, 1164, 880,
        1170, 810, 1170, 790, 1120, 690, 1090, 640, 1040, 624, 980, 690, 880, 750, 770,
      ]),
      tap: pairs([
        812, 646, 730, 762, 604, 974, 618, 1042, 680, 1108, 802, 1188, 990, 1182, 1052, 1156, 1122,
        1090, 1120, 1024, 842, 658,
      ]),
    },
    {
      node: 38,
      name: "Pharaoh's Riches",
      centre: [1250, 540],
      plate: [1288, 160],
      plateTight: [960, 197],
      plateNarrow: true,
      outline: pairs([
        1140, 360, 1200, 300, 1220, 276, 1300, 276, 1380, 340, 1424, 370, 1440, 460, 1450, 530,
        1470, 600, 1480, 660, 1430, 690, 1380, 720, 1320, 750, 1240, 744, 1170, 720, 1110, 740,
        1060, 730, 1050, 680, 1060, 600, 1040, 470, 1056, 410,
      ]),
    },
    {
      node: 39,
      name: "Cleopatra's Palace",
      centre: [1620, 1040],
      plate: [1546, 1396],
      plateTight: [1592, 1396],
      outline: pairs([
        1572, 784, 1660, 840, 1700, 850, 1740, 860, 1800, 900, 1850, 960, 1870, 1040, 1880, 1110,
        1850, 1160, 1800, 1200, 1760, 1250, 1740, 1320, 1760, 1380, 1680, 1380, 1600, 1380, 1540,
        1320, 1490, 1280, 1400, 1280, 1364, 1240, 1370, 1160, 1376, 1040, 1390, 940, 1430, 890,
        1480, 890, 1500, 840, 1540, 810,
      ]),
      tap: pairs([
        1564, 766, 1420, 872, 1370, 934, 1356, 1028, 1344, 1246, 1388, 1296, 1592, 1398, 1768, 1398,
        1788, 1370, 1900, 1118, 1872, 960, 1810, 882, 1756, 846, 1582, 766,
      ]),
    },
    {
      node: 40,
      name: 'Book of the Dead',
      centre: [2100, 600],
      plate: [2040, 226],
      plateTight: [2000, 198],
      outline: pairs([
        2000, 380, 2080, 340, 2150, 370, 2190, 410, 2250, 450, 2310, 490, 2320, 560, 2330, 660,
        2350, 720, 2340, 790, 2300, 820, 2240, 790, 2200, 850, 2120, 860, 2040, 840, 1970, 850,
        1900, 840, 1860, 800, 1900, 760, 1870, 680, 1860, 600, 1880, 520, 1930, 460,
      ]),
    },
    {
      node: 41,
      name: 'Anubis Underworld',
      centre: [2280, 1180],
      plate: [2312, 1412],
      plateTight: [2360, 1412],
      outline: pairs([
        2260, 924, 2300, 936, 2330, 980, 2380, 1000, 2430, 1030, 2480, 1080, 2524, 1120, 2544, 1180,
        2524, 1250, 2500, 1300, 2460, 1330, 2390, 1380, 2320, 1400, 2240, 1410, 2160, 1400, 2090,
        1390, 2040, 1360, 2024, 1300, 2030, 1220, 2040, 1150, 2090, 1080, 2150, 1040, 2200, 980,
      ]),
      tap: pairs([
        2542, 1110, 2440, 1012, 2316, 922, 2252, 906, 2070, 1072, 2024, 1136, 2004, 1282, 2018,
        1362, 2082, 1408, 2234, 1430, 2312, 1422, 2394, 1400, 2518, 1310, 2564, 1188,
      ]),
    },
    {
      node: 42,
      name: 'Eye of Ra',
      centre: [2780, 740],
      plate: [2768, 386],
      plateTight: [2632, 356],
      plateNarrow: true,
      outline: pairs([
        2680, 520, 2750, 500, 2840, 510, 2910, 560, 2950, 600, 3000, 620, 3016, 700, 3010, 800,
        3000, 880, 2950, 940, 2880, 960, 2800, 960, 2760, 996, 2690, 980, 2660, 940, 2590, 910,
        2544, 880, 2544, 800, 2580, 700, 2600, 630, 2640, 570,
      ]),
      tap: pairs([
        2748, 480, 2668, 504, 2584, 616, 2524, 792, 2524, 886, 2674, 994, 2762, 1016, 2954, 960,
        3020, 886, 3036, 690, 3022, 620, 3010, 602, 2848, 492,
      ]),
    },
  ],
  road: pairs([
    0, 876, 80, 932, 200, 972, 340, 964, 460, 916, 580, 860, 670, 820, 800, 800, 940, 820, 1080,
    850, 1180, 876, 1260, 880, 1350, 820, 1410, 770, 1490, 748, 1600, 752, 1700, 790, 1800, 850,
    1910, 910, 2060, 940, 2200, 910, 2340, 900, 2440, 930, 2560, 996, 2680, 1060, 2830, 1090, 2980,
    1070, 3072, 1060,
  ]),
  roadWidth: 100,
  still: [
    {
      name: 'palace landing stage, deck and posts',
      outline: pairs([
        1510, 1290, 1530, 1262, 1700, 1248, 1770, 1280, 1785, 1335, 1775, 1355, 1740, 1352, 1700,
        1345, 1650, 1395, 1600, 1390, 1560, 1370, 1525, 1340,
      ]),
    },
  ],
  lanternPosts: pairs([
    79, 868, 279, 1020, 488, 960, 612, 789, 943, 732, 1151, 922, 1411, 837, 1692, 725, 1912, 982,
    2117, 981, 2366, 853, 2546, 1061, 2825, 1036, 2975, 1012,
  ]),
  plateBox: [420, 110],
  plateBoxTight: [528, 138],
  plateBoxNarrow: [394, 138],
  ambient: '/world/egypt/ambient/v1',
  ambientFiles: ['life.json', 'life.webp', 'sway.webp', 'water.webp'],
  // Sunscar Sands' road friends' kinds: no camels, meerkats, cats or cat statues
  // (a horse-drawn cart, horses, ibises, traders and a river barge stand in).
  forbidden: ['camel', 'dromedary', 'meerkat', 'cat', 'kitten', 'sphinx'],
};
